(() => {
  if (customElements.get("cart-upsell")) return;

  const DEBUG = true;
  const log = (...args) => {
    if (DEBUG) console.log("[cart-upsell]", ...args);
  };

  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.abortController = null;
      this.unsubscribeCartUpdate = null;
      this.instanceId = Math.random().toString(36).slice(2, 8);
    }

    connectedCallback() {
      this.listElement = this.querySelector("[data-upsell-list]");
      this.loadingElement = this.querySelector("[data-upsell-loading]");
      this.minicart = document.querySelector("mini-cart");

      log(`#${this.instanceId} connectedCallback`, {
        source: this.dataset.source,
        sectionId: this.dataset.sectionId,
        limit: this.dataset.limit,
        hasListElement: !!this.listElement,
        hasMinicart: !!this.minicart,
      });

      if (!this.listElement) return;

      this.listElement.addEventListener("click", this.onClick);
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      document.addEventListener("cart:item-add", this.onCartUpdate);
      document.addEventListener("cart:item-remove", this.onCartUpdate);
      document.addEventListener("cart:item-quantity-update", this.onCartUpdate);

      this.loadProducts();
    }

    disconnectedCallback() {
      log(`#${this.instanceId} disconnectedCallback`);
      this.listElement?.removeEventListener("click", this.onClick);
      this.unsubscribeCartUpdate?.();
      document.removeEventListener("cart:item-add", this.onCartUpdate);
      document.removeEventListener("cart:item-remove", this.onCartUpdate);
      document.removeEventListener("cart:item-quantity-update", this.onCartUpdate);
      this.abortController?.abort();
    }

    onCartUpdate(event) {
      log(`#${this.instanceId} cart update event ->`, event?.type || "pub-sub cartUpdate");
      this.loadProducts();
    }

    onClick(event) {
      const button = event.target.closest(".js-mini-cart-upsell-add");
      if (!button || button.classList.contains("loading")) return;

      const variantId = button.dataset.cartVariantId || button.dataset.variantId;
      if (!variantId) return;

      event.preventDefault();
      this.addToCart(variantId, button);
    }

    async getCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      return response.json();
    }

    getReferenceProductId(cart) {
      if (!cart?.items?.length) return null;

      if (this.dataset.referenceProduct === "first_added") {
        return cart.items[0].product_id;
      }

      return cart.items[cart.items.length - 1].product_id;
    }

    getCartProductIdsOrdered(cart) {
      if (!cart?.items?.length) return [];

      const items = this.dataset.referenceProduct === "first_added"
        ? [...cart.items]
        : [...cart.items].reverse();

      const seen = new Set();
      const ordered = [];

      for (const item of items) {
        if (seen.has(item.product_id)) continue;
        seen.add(item.product_id);
        ordered.push(item.product_id);
      }

      return ordered;
    }

    setLoading(isLoading) {
      if (this.loadingElement) {
        this.loadingElement.hidden = !isLoading;
      }
    }

    showBlock(hasProducts) {
      log(`#${this.instanceId} showBlock ->`, hasProducts ? "visible" : "hidden");
      this.hidden = !hasProducts;
    }

    buildRecommendationsUrl(productId) {
      const limit = this.dataset.limit || "4";

      return `${this.dataset.recommendationsUrl}?section_id=${this.dataset.sectionId}&product_id=${productId}&limit=${limit}&intent=related`;
    }

    getRecommendationsUrl(cart) {
      const productId = this.getReferenceProductId(cart);
      if (!productId) return null;

      return this.buildRecommendationsUrl(productId);
    }

    async fetchRecommendationsForCart(cart, signal) {
      const productIds = this.getCartProductIdsOrdered(cart);
      log(`#${this.instanceId} recommendation source candidates`, productIds);

      for (const productId of productIds) {
        const url = this.buildRecommendationsUrl(productId);
        log(`#${this.instanceId} trying recommendation source`, productId, url);

        const result = await this.fetchSectionHtml(url, signal);
        log(`#${this.instanceId} candidate slide count`, productId, "=", result.slideCount);

        if (result.slideCount > 0) {
          return result;
        }
      }

      return { html: "", slideCount: 0 };
    }

    async loadProducts() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.setLoading(true);
      log(`#${this.instanceId} loadProducts START`);

      try {
        const cart = await this.getCart();
        log(`#${this.instanceId} cart fetched`, { itemCount: cart.item_count });

        if (!cart.item_count) {
          this.showBlock(false);
          return;
        }

        let html = "";
        let slideCount = 0;
        const source = this.dataset.source;
        log(`#${this.instanceId} source ->`, source);

        if (source === "variant_metafield") {
          const metafieldUrl = `${window.location.pathname}?sections=${this.dataset.sectionId}`;
          log(`#${this.instanceId} fetching metafield section`, metafieldUrl);

          const metafield = await this.fetchSectionHtml(metafieldUrl, signal, true);
          html = metafield.html;
          slideCount = metafield.slideCount;
          log(`#${this.instanceId} metafield slide count =`, slideCount);

          if (slideCount === 0) {
            log(`#${this.instanceId} fallback recommendations requested (cart-wide)`);
            const recommendations = await this.fetchRecommendationsForCart(cart, signal);
            html = recommendations.html;
            slideCount = recommendations.slideCount;
            log(`#${this.instanceId} fallback recommendation slide count =`, slideCount);
          }
        } else {
          const recommendationsUrl = this.getRecommendationsUrl(cart);
          log(`#${this.instanceId} recommendations requested`, recommendationsUrl);

          if (recommendationsUrl) {
            const recommendations = await this.fetchSectionHtml(recommendationsUrl, signal);
            html = recommendations.html;
            slideCount = recommendations.slideCount;
            log(`#${this.instanceId} recommendation slide count =`, slideCount);
          }
        }

        const hasProducts = slideCount > 0;

        this.listElement.innerHTML = hasProducts
          ? html
          : `<div class="mini-cart-upsell__loading" data-upsell-loading hidden></div>`;

        this.loadingElement = this.querySelector("[data-upsell-loading]");
        this.showBlock(hasProducts);
        log(`#${this.instanceId} loadProducts END`, { slideCount, hasProducts });
      } catch (error) {
        if (error.name === "AbortError") {
          log(`#${this.instanceId} loadProducts aborted`);
          return;
        }
        log(`#${this.instanceId} loadProducts failed`, error);
        this.showBlock(false);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchSectionHtml(url, signal, isSectionsApi = false) {
      const response = await fetch(url, { signal });
      const text = await response.text();

      let source = text;
      if (isSectionsApi) {
        try {
          const json = JSON.parse(text);
          source = json[this.dataset.sectionId] || "";
        } catch (error) {
          source = "";
        }
      }

      return this.extractProductsHtml(source);
    }

    extractProductsHtml(html) {
      const emptyResult = { html: "", slideCount: 0 };

      if (!html?.trim()) return emptyResult;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const products = doc.querySelector("[data-upsell-products]");
      if (!products) return emptyResult;

      const slideCount = products.querySelectorAll(".mini-cart-upsell-products__slide").length;
      if (slideCount === 0) return emptyResult;

      return {
        html: products.innerHTML.trim(),
        slideCount,
      };
    }

    /*
      Cryptic Air fix, 2026-07-30. Stock Athens only ever re-rendered the mini-cart
      drawer after an upsell add, so on the /cart page the item went into the cart
      server-side but nothing on screen changed until the customer reloaded. These
      are the cart page's own sections, taken from cart-coupon.js which already
      solves the same problem. The filter drops anything not on the current page,
      so this is a no-op inside the drawer.
    */
    cartPageSectionsToRender() {
      return [
        { id: "cart-page-title-wrap", selector: ".page-title-wrap" },
        { id: "main-cart-items", selector: ".js-contents" },
        { id: "main-cart-footer", selector: ".js-contents" },
        { id: "header", selector: ".head-slot-cart-link" },
        { id: "mobile-dock", selector: ".head-slot-cart-link" },
      ]
        .map((entry) => ({
          ...entry,
          section: document.getElementById(entry.id)?.dataset.id,
        }))
        .filter((entry) => {
          const element = document.getElementById(entry.id);
          return element && entry.section && element.querySelector(entry.selector);
        });
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(selector)?.innerHTML;
    }

    renderCartPageSections(data) {
      if (!data?.sections) return;

      this.cartPageSectionsToRender().forEach(({ id, section, selector }) => {
        const target = document.getElementById(id)?.querySelector(selector);
        const html = data.sections[section];
        if (!target || !html) return;

        const inner = this.getSectionInnerHTML(html, selector);
        if (inner != null) target.innerHTML = inner;
      });
    }

    async addToCart(variantId, button) {
      button.classList.add("loading");
      button.setAttribute("aria-disabled", "true");

      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");

      // Ask for the drawer's sections and the cart page's sections in one request.
      // A Set because `header` is in both lists and Shopify rejects duplicates.
      const sections = new Set();
      if (this.minicart) {
        this.minicart
          .getSectionsToRender()
          .forEach((section) => section.section && sections.add(section.section));
      }
      this.cartPageSectionsToRender().forEach((section) => sections.add(section.section));

      formData.append("sections", [...sections].join(","));
      formData.append("sections_url", window.location.pathname);

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const data = await response.json();

        if (data.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "cart-upsell",
            productVariantId: variantId,
            errors: data.errors || data.description,
            message: data.message,
          });

          if (this.minicart?.setError) {
            this.minicart.setError(data.description || window.cartStrings.error);
          }

          return;
        }

        const miniCartError = document.getElementById("mini-cart-error");
        if (miniCartError) {
          miniCartError.textContent = "";
        }

        this.minicart?.renderContents(data);
        this.renderCartPageSections(data);

        document.dispatchEvent(
          new CustomEvent("cart:item-add", {
            detail: {
              items: [data],
              context: "CartUpsell",
            },
          }),
        );

        publish(PUB_SUB_EVENTS.cartItemAdd, {
          source: "CartUpsell",
          items: [data],
        });
      } catch (error) {
        if (this.minicart?.setError) {
          this.minicart.setError(window.cartStrings.error);
        }
      } finally {
        button.classList.remove("loading");
        button.removeAttribute("aria-disabled");
      }
    }
  }

  customElements.define("cart-upsell", CartUpsell);
})();
